import {
  CheckoutApiResponse,
  PriceComparisonLine,
  StripeApi,
} from "@/api/stripe.api";
import { getPreemptiveShippingTypeByDimensions } from "@/components/CheckoutForm/multipleCalculatedHeuristic";
import { $checkout } from "@/stores/checkout";
import {
  Address,
  Cart,
  CheckoutSession,
  StripeShippingOption,
} from "@/utils/types/types";
import { useStore } from "@nanostores/react";
import { WebUser } from "@prisma/client";
import { useCheckout } from "@stripe/react-stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type ShippingType = {
  value:
    | "shipToMyAddress"
    | "pickup"
    | "shipToOtherAddress"
    | "oversized"
    | "multiple-calculated";
  label: string;
};

export type ShippingOption = {
  value: string;
  label: string;
  display_name?: string;
  price?: number;
  currency?: string;
  originalOption?: StripeShippingOption;
};

export type BillingAddressType = {
  value: "shipping-address" | "billing-address";
  label: string;
};

export type CheckoutContext = ReturnType<typeof useCheckoutContext>;

export const TRIBECA_ADDRESS = {
  name: "Tribeca Printworks",
  address: {
    line1: "333 Hudson St, rm 201",
    line2: "",
    city: "New York",
    state: "NY",
    postal_code: "10013",
    country: "US",
  } satisfies Address,
};

type UseCheckoutContextProps = {
  session: CheckoutSession;
  shippingOptions: ShippingOption[];
  shippingPickupOption: ShippingOption;
  shippingOversizedOption?: ShippingOption | null;
  user?: WebUser;
  cartDimensions?: { width: number; height: number };
  cart?: Cart; // Cart data for subtotal calculation
  onCheckoutReplaced?: (checkoutData: CheckoutApiResponse) => void;
  priceComparison?: PriceComparisonLine[];
};

export function useCheckoutContext({
  session,
  shippingOptions,
  shippingPickupOption,
  shippingOversizedOption,
  user,
  cartDimensions, // Keep for backward compatibility
  cart,
  onCheckoutReplaced,
  priceComparison,
}: UseCheckoutContextProps) {
  const getInitialReviewCompleted = () => {
    if (typeof window === "undefined") return false;
    // FIX 2026-08-05 (AU customer, "totals are still syncing" dead end):
    // every checkout page load mints a FRESH session with
    // isFinalized:"false" (the loader's POST /checkout), so a restored
    // "review completed" flag from a previous visit would surface a Pay
    // button that can only throw — nothing re-finalizes without Review's
    // Save & Continue. Never restore the flag for an unfinalized session.
    if (session.metadata.isFinalized !== "true") return false;
    return sessionStorage.getItem("checkout_review_completed") === "true";
  };

  const checkout = useCheckout();
  const persistedCheckout = useStore($checkout);

  // Note: cartDimensions is kept for backward compatibility but not used in new implementation
  void cartDimensions;

  const [activeStep, setActiveStep] = useState("1");
  const [collapsePanel, setCollapsePanel] = useState(false);
  const [reviewCompleted, setReviewCompleted] = useState(
    getInitialReviewCompleted
  );
  const [isUpdatingPricing, setIsUpdatingPricing] = useState(false);

  // Calculate cart subtotal for free shipping qualification
  const cartSubtotal = useMemo(() => {
    if (!cart?.lineItems) return 0;
    return cart.lineItems.reduce((total: number, item) => {
      const price = Number(item.itemJson.price || 0);
      const quantity = item.quantity || 0;
      return total + price * quantity;
    }, 0);
  }, [cart]);

  // Safety timeout to prevent loading state from getting stuck
  useEffect(() => {
    if (isUpdatingPricing) {
      const timeout = setTimeout(() => {
        setIsUpdatingPricing(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isUpdatingPricing]);

  const [updatedShippingOptions, setUpdatedShippingOptions] =
    useState<ShippingOption[]>(shippingOptions);
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [isFinalizingSession, setIsFinalizingSession] = useState(false);
  const isFinalizingSessionRef = useRef(false);
  const [shippingStatus, setShippingStatus] = useState("standard");
  // Signature of the cart contents that affect shipping quotes. Quantity or
  // line-item edits kept serving STALE delivery options before (same cart.id,
  // new contents — 2026-07-15 fix): this feeds the fetch effect's deps.
  const cartLineItemsSignature = useMemo(
    () =>
      JSON.stringify(
        (cart?.lineItems || []).map(li => [li.id, li.quantity ?? 0])
      ),
    [cart]
  );
  const latestShippingCalcRequestIdRef = useRef(0);
  const shippingCalcInFlightCountRef = useRef(0);
  // FIX 2026-07-19 (PIN #4): shippingStatus starts as "standard" before any
  // backend response — the un-latch below must not fire off that placeholder
  // (a persisted oversized type would flap: un-latch on mount, re-latch when
  // the real status lands). Only un-latch once a real status has resolved.
  const shippingStatusResolvedRef = useRef(false);
  // FIX 2026-07-24 (snap-back): PIN #4's un-latch below picked its restore
  // target by comparing the delivery fields to the billing fields — stale or
  // matching fields sent an explicit "Ship To A Different Address" click
  // back to "Ship To My Billing Address" (click-steal, reproduced 7/23–24).
  // Track the last ship-to type that reached data.shippingType from OUTSIDE
  // this hook's own writes (the customer's radio click in the shipping form)
  // and prefer it as the restore target.
  const lastExplicitShipToRef = useRef<ShippingType["value"] | null>(null);
  const selfWroteShippingTypeRef = useRef(false);

  useEffect(() => {
    if (!cart?.id) return;
    StripeApi.getCartShippingStatus({ cartId: cart.id })
      .then(res => {
        const status = res.data.shippingStatus;
        shippingStatusResolvedRef.current = true;
        setShippingStatus(status);
        if (status === "exceeds_table") {
          // Set Tribeca address immediately so Stripe can calculate tax
          // regardless of which accordion step is currently visible.
          const optionId =
            shippingOversizedOption?.value || shippingPickupOption.value;
          checkout
            .updateShippingAddress(TRIBECA_ADDRESS)
            .then(() => checkout.updateShippingOption(optionId))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [cart?.id]); // intentionally omit checkout/shippingOversizedOption/shippingPickupOption to run once

  // Checkout prefill (2026-08-26, Michael-approved): logged-in accounts stop
  // retyping their details every order. Precedence per field: what the
  // customer already typed on this browser (persisted localStorage) >
  // account record > blank. billingDetails is the canonical Stripe-shaped
  // record — the same one Profile → User Details, the legacy-contact seed,
  // and the Billing step's save-back all write.
  const savedBilling = (user?.billingDetails ?? null) as {
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  } | null;
  // The address block prefills ATOMICALLY: any persisted billing-address
  // field means the customer touched the block on this browser, and mixing
  // their partial entries with the account's address could pair a state
  // with the wrong country (the billing form validates state/zip AGAINST
  // country). All-or-nothing keeps the pair coherent.
  const billingBlockUntouched =
    !persistedCheckout.billingStreet &&
    !persistedCheckout.billingCity &&
    !persistedCheckout.billingZipcode &&
    !persistedCheckout.billingState &&
    !persistedCheckout.billingCountry;
  const prefillAddr = billingBlockUntouched ? savedBilling?.address : null;

  const [data, setData] = useState({
    parentOrderNo: persistedCheckout.parentOrderNo || "",
    promocode: persistedCheckout.promocode || "",
    phoneNumber: persistedCheckout.phoneNumber || savedBilling?.phone || "",
    email: persistedCheckout.email || user?.email || "",
    firstName:
      persistedCheckout.firstName ||
      user?.firstName ||
      savedBilling?.firstName ||
      "",
    lastName:
      persistedCheckout.lastName ||
      user?.lastName ||
      savedBilling?.lastName ||
      "",
    companyName: persistedCheckout.companyName || "",
    billingStreet: persistedCheckout.billingStreet || prefillAddr?.line1 || "",
    billingApartment:
      persistedCheckout.billingApartment || prefillAddr?.line2 || "",
    billingCountry:
      persistedCheckout.billingCountry || prefillAddr?.country || "",
    billingState: persistedCheckout.billingState || prefillAddr?.state || "",
    billingCity: persistedCheckout.billingCity || prefillAddr?.city || "",
    billingZipcode:
      persistedCheckout.billingZipcode || prefillAddr?.postal_code || "",
    shippingFirstName: persistedCheckout.shippingFirstName || "",
    shippingLastName: persistedCheckout.shippingLastName || "",
    shippingCompanyName: persistedCheckout.shippingCompanyName || "",
    shippingStreet: persistedCheckout.shippingStreet || "",
    shippingApartment: persistedCheckout.shippingApartment || "",
    shippingCountry: persistedCheckout.shippingCountry || "",
    shippingState: persistedCheckout.shippingState || "",
    shippingCity: persistedCheckout.shippingCity || "",
    shippingZipcode: persistedCheckout.shippingZipcode || "",
    deliveryFirstName: persistedCheckout.deliveryFirstName || "",
    deliveryLastName: persistedCheckout.deliveryLastName || "",
    deliveryCompanyName: persistedCheckout.deliveryCompanyName || "",
    deliveryStreet: persistedCheckout.deliveryStreet || "",
    deliveryApartment: persistedCheckout.deliveryApartment || "",
    deliveryCountry: persistedCheckout.deliveryCountry || "",
    deliveryCity: persistedCheckout.deliveryCity || "",
    deliveryState: persistedCheckout.deliveryState || "",
    deliveryZipcode: persistedCheckout.deliveryZipcode || "",
    whiteLabel: persistedCheckout.whiteLabel || false,
    gift: persistedCheckout.gift || false,
    shippingType:
      persistedCheckout.shippingType || ("pickup" as ShippingType["value"]),
    shippingOption:
      persistedCheckout.shippingOption || shippingPickupOption.value,
    billingAddressType:
      persistedCheckout.billingAddressType ||
      ("billing-address" as BillingAddressType["value"]),
    billingAddress: {} as Record<string, unknown>,
    note: persistedCheckout.note || "",
  });

  // FIX 2026-07-24: record explicit ship-to selections. Writes from this
  // hook's own latch/restore effect raise selfWroteShippingTypeRef first and
  // are skipped; everything else reaching a ship-to value is the customer.
  useEffect(() => {
    if (
      data.shippingType !== "shipToMyAddress" &&
      data.shippingType !== "shipToOtherAddress"
    )
      return;
    if (selfWroteShippingTypeRef.current) {
      selfWroteShippingTypeRef.current = false;
      return;
    }
    lastExplicitShipToRef.current = data.shippingType;
  }, [data.shippingType]);

  useEffect(() => {
    const isSkipShipping = !!(cart as any)?.skipShipping;
    if (isSkipShipping) return;
    const preemptiveShippingType =
      shippingStatus === "exceeds_table"
        ? "oversized"
        : shippingStatus === "multiple_calculated"
          ? "multiple-calculated"
          : getPreemptiveShippingTypeByDimensions(cart);
    // FIX 2026-07-19 (PIN #4): un-latch the oversized/quote state. Entering
    // it swaps the Stripe session address to the TRIBECA address (tax) with
    // no reverse path, so leaving an HI/AK or oversized state left the UI
    // stuck — UPS hidden, messenger-only (Sim 9). When the backend says
    // "standard" and the cart's dimensions don't warrant a quote type,
    // reset shippingType to the address-appropriate ship-to value AND
    // restore the customer's real address to the Stripe session.
    if (!preemptiveShippingType) {
      // v2 (2026-07-19 U5 retest): the guard was `shippingStatus ===
      // "standard"`, but non-quote recalcs can also land "free_shipping" /
      // "calculated" — the latch survived those. Any resolved status that
      // yields NO preemptive type un-latches. The restored address is the
      // DELIVERY-form address (the one that cleared the gate): restoring a
      // stale shipping-field address could re-fire the gate in a loop.
      if (
        shippingStatusResolvedRef.current &&
        (data.shippingType === "oversized" ||
          data.shippingType === "multiple-calculated")
      ) {
        const hasDeliveryAddress = Boolean(
          data.deliveryStreet?.trim() &&
            data.deliveryCity?.trim() &&
            data.deliveryState?.trim() &&
            data.deliveryZipcode?.trim()
        );
        const deliveryMatchesBilling =
          hasDeliveryAddress &&
          data.deliveryStreet?.trim() === data.billingStreet?.trim() &&
          data.deliveryZipcode?.trim() === data.billingZipcode?.trim();
        // FIX 2026-07-24: an explicit customer click outranks the address
        // heuristic — the heuristic only decides when no click was recorded.
        const restoredType: ShippingType["value"] =
          lastExplicitShipToRef.current ??
          (hasDeliveryAddress && !deliveryMatchesBilling
            ? "shipToOtherAddress"
            : "shipToMyAddress");
        // FIX 2026-07-24: restoring to ship-to-other WITHOUT a usable
        // delivery address must not push empty (or stale gate-firing) fields
        // to Stripe — restore the TYPE only and let the form's recalc set
        // the address once the customer completes it.
        const restoredAddress =
          restoredType === "shipToOtherAddress"
            ? !hasDeliveryAddress
              ? null
              : {
                  name: `${data.deliveryFirstName} ${data.deliveryLastName}`,
                  address: {
                    line1: data.deliveryStreet.trim(),
                    line2: data.deliveryApartment || "",
                    city: data.deliveryCity.trim(),
                    state: data.deliveryState,
                    postal_code: data.deliveryZipcode,
                    country: data.deliveryCountry || "US",
                  },
                }
            : {
                name: `${data.firstName} ${data.lastName}`,
                address: {
                  line1: data.billingStreet.trim(),
                  line2: data.billingApartment || "",
                  city: data.billingCity.trim(),
                  state: data.billingState,
                  postal_code: data.billingZipcode,
                  country: data.billingCountry || "US",
                },
              };
        const restoredOption =
          updatedShippingOptions.find(
            option =>
              option.value && option.value !== shippingPickupOption.value
          )?.value ||
          shippingOptions[0]?.value ||
          "";
        selfWroteShippingTypeRef.current = true;
        setData(prev => ({
          ...prev,
          shippingType: restoredType,
          shippingOption: restoredOption,
          // Carry the gate-clearing delivery address into the restored
          // ship-to-other flow so the visible form matches what's charged.
          // FIX 2026-07-24: only when a delivery address exists — carrying
          // empty fields would blank an address the customer already typed.
          ...(restoredType === "shipToOtherAddress" && hasDeliveryAddress
            ? {
                shippingFirstName: prev.deliveryFirstName,
                shippingLastName: prev.deliveryLastName,
                shippingCompanyName: prev.deliveryCompanyName,
                shippingStreet: prev.deliveryStreet,
                shippingApartment: prev.deliveryApartment,
                shippingCountry: prev.deliveryCountry,
                shippingCity: prev.deliveryCity,
                shippingState: prev.deliveryState,
                shippingZipcode: prev.deliveryZipcode,
              }
            : {}),
        }));
        setIsUpdatingPricing(true);
        (restoredAddress
          ? checkout.updateShippingAddress(restoredAddress)
          : Promise.resolve()
        )
          .then(() =>
            restoredOption
              ? checkout.updateShippingOption(restoredOption)
              : undefined
          )
          .catch(() => {})
          .finally(() => {
            setIsUpdatingPricing(false);
          });
      }
      return;
    }
    if (data.shippingType === preemptiveShippingType) {
      return;
    }
    if (
      (preemptiveShippingType === "multiple-calculated" ||
        preemptiveShippingType === "oversized") &&
      data.shippingType === "pickup"
    ) {
      return;
    }
    const shippingOptionId =
      shippingOversizedOption?.value || shippingPickupOption.value;
    // (No selfWrote flag here: this write is a QUOTE type, which the
    // recorder effect ignores — flagging it would swallow the customer's
    // NEXT real click instead.)
    setData(prev => ({
      ...prev,
      shippingType: preemptiveShippingType,
      shippingOption: shippingOptionId,
      billingAddressType: "shipping-address",
    }));
    setIsUpdatingPricing(true);
    checkout
      .updateShippingAddress(TRIBECA_ADDRESS)
      .then(() => checkout.updateShippingOption(shippingOptionId))
      .catch(() => {})
      .finally(() => {
        setIsUpdatingPricing(false);
      });
  }, [
    cart,
    shippingStatus,
    data.shippingType,
    checkout,
    shippingOversizedOption?.value,
    shippingPickupOption.value,
  ]);

  // Sync local state to persisted store whenever data changes
  useEffect(() => {
    $checkout.setKey("phoneNumber", data.phoneNumber);
    $checkout.setKey("parentOrderNo", data.parentOrderNo);
    $checkout.setKey("email", data.email);
    $checkout.setKey("firstName", data.firstName);
    $checkout.setKey("lastName", data.lastName);
    $checkout.setKey("companyName", data.companyName);
    $checkout.setKey("billingStreet", data.billingStreet);
    $checkout.setKey("billingApartment", data.billingApartment);
    $checkout.setKey("billingCountry", data.billingCountry);
    $checkout.setKey("billingState", data.billingState);
    $checkout.setKey("billingCity", data.billingCity);
    $checkout.setKey("billingZipcode", data.billingZipcode);
    $checkout.setKey("shippingFirstName", data.shippingFirstName);
    $checkout.setKey("shippingLastName", data.shippingLastName);
    $checkout.setKey("shippingCompanyName", data.shippingCompanyName);
    $checkout.setKey("shippingStreet", data.shippingStreet);
    $checkout.setKey("shippingApartment", data.shippingApartment);
    $checkout.setKey("shippingCountry", data.shippingCountry);
    $checkout.setKey("shippingState", data.shippingState);
    $checkout.setKey("shippingCity", data.shippingCity);
    $checkout.setKey("shippingZipcode", data.shippingZipcode);
    $checkout.setKey("deliveryFirstName", data.deliveryFirstName);
    $checkout.setKey("deliveryLastName", data.deliveryLastName);
    $checkout.setKey("deliveryCompanyName", data.deliveryCompanyName);
    $checkout.setKey("deliveryStreet", data.deliveryStreet);
    $checkout.setKey("deliveryApartment", data.deliveryApartment);
    $checkout.setKey("deliveryCountry", data.deliveryCountry);
    $checkout.setKey("deliveryCity", data.deliveryCity);
    $checkout.setKey("deliveryState", data.deliveryState);
    $checkout.setKey("deliveryZipcode", data.deliveryZipcode);
    $checkout.setKey("whiteLabel", data.whiteLabel);
    $checkout.setKey("gift", data.gift);
    $checkout.setKey("shippingType", data.shippingType);
    $checkout.setKey("shippingOption", data.shippingOption);
    $checkout.setKey("billingAddressType", data.billingAddressType);
    $checkout.setKey("note", data.note);
    $checkout.setKey("promocode", data.promocode);
  }, [data]);

  const isBillingSameAsShipping =
    data.shippingType === "shipToMyAddress" &&
    data.billingAddressType === "shipping-address";

  // Calculate shipping prices and get delivery options based on shipping address
  const calculateShippingPrices = useCallback(async () => {
    if (data.shippingType === "pickup" || !cart?.id) {
      return;
    }

    // Extract shipping info based on shipping type
    const shippingInfo = (() => {
      if (
        data.shippingType === "multiple-calculated" ||
        data.shippingType === "oversized"
      ) {
        // F1 (2026-07-16) + oversized (2026-07-18): quote-arranged carts
        // still get a real messenger quote for their DELIVERY address (the
        // messenger has no UPS box ceiling; the pricer handles multi-piece
        // carts) — only UPS/oversized remains contact-for-quote.
        return {
          firstName: data.deliveryFirstName || "",
          lastName: data.deliveryLastName || "",
          street: data.deliveryStreet || "",
          city: data.deliveryCity || "",
          state: data.deliveryState || "",
          zipcode: data.deliveryZipcode || "",
          country: data.deliveryCountry || "US",
        };
      } else if (data.shippingType === "shipToOtherAddress") {
        // Use separate shipping address
        return {
          firstName: data.shippingFirstName || "",
          lastName: data.shippingLastName || "",
          street: data.shippingStreet || "",
          city: data.shippingCity || "",
          state: data.shippingState || "",
          zipcode: data.shippingZipcode || "",
          country: data.shippingCountry || "US",
        };
      } else {
        // Use billing address as shipping address
        return {
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          street: data.billingStreet || "",
          city: data.billingCity || "",
          state: data.billingState || "",
          zipcode: data.billingZipcode || "",
          country: data.billingCountry || "US",
        };
      }
    })();

    // When skipShipping is true we don't need an address — the server returns
    // the free shipping option regardless of the destination zipcode.
    const isSkipShipping = !!(cart as any)?.skipShipping;
    if (
      !isSkipShipping &&
      (!shippingInfo.zipcode || shippingInfo.zipcode.length < 5)
    ) {
      return;
    }

    const requestId = ++latestShippingCalcRequestIdRef.current;
    shippingCalcInFlightCountRef.current += 1;
    setIsCalculatingShipping(true);

    try {
      // Get delivery options based on shipping address
      const response = await StripeApi.getDeliveryMethods({
        cartId: cart.id,
        shippingInfo,
      });
      if (requestId !== latestShippingCalcRequestIdRef.current) {
        console.info("[ShippingDebug] Ignoring stale shipping response", {
          requestId,
          latestRequestId: latestShippingCalcRequestIdRef.current,
          zipcode: shippingInfo.zipcode,
        });
        return;
      }
      // Transform delivery methods to ShippingOptions
      const transformedShippingOptions = response.data.deliveryMethods.map(
        method => ({
          value: method.id,
          label: method.key,
          display_name: method.displayName,
          price: method.priceInCents,
          currency: method.currency,
          // Don't include originalOption since it's a different format now
        })
      );

      const transformedPickupOption = response.data.pickupOption
        ? {
            value: response.data.pickupOption.id,
            label: response.data.pickupOption.key,
            display_name: response.data.pickupOption.displayName,
            price: response.data.pickupOption.priceInCents,
            currency: response.data.pickupOption.currency,
          }
        : null;

      // Keep Stripe checkout session shipping options in sync with recalculated rates
      // so the selected dynamic option can be charged at confirm time.
      // For exceeds_table carts, always include the oversized rate so it isn't
      // evicted from the session before updateShippingOption can select it.
      const shippingRateIds = Array.from(
        new Set(
          [
            response.data.pickupOption?.id,
            response.data.oversizedOption?.id,
            ...response.data.deliveryMethods.map(method => method.id),
          ].filter((id): id is string => Boolean(id))
        )
      );
      if (shippingRateIds.length > 0) {
        await StripeApi.updateCheckout(session.id, { shippingRateIds });
      }
      if (requestId !== latestShippingCalcRequestIdRef.current) {
        console.info(
          "[ShippingDebug] Ignoring stale post-update shipping response",
          {
            requestId,
            latestRequestId: latestShippingCalcRequestIdRef.current,
            zipcode: shippingInfo.zipcode,
          }
        );
        return;
      }

      // Update the shipping options and status from the API
      setUpdatedShippingOptions(transformedShippingOptions);
      shippingStatusResolvedRef.current = true; // PIN #4: real status landed
      setShippingStatus(response.data.cartInfo?.shippingStatus || "standard");

      // Ensure current selection remains valid when options change
      const stillExists = transformedShippingOptions.some(
        o => o.value === data.shippingOption
      );
      // F1: in the quote-arranged states the default selection is the $0
      // contact-for-quote flow — never auto-select the (priced) messenger
      // option; the customer has to opt into it explicitly.
      if (
        !stillExists &&
        data.shippingType !== "multiple-calculated" &&
        data.shippingType !== "oversized"
      ) {
        // If current selection is not available, fall back to first available option
        const fallback =
          transformedShippingOptions[0]?.value ||
          transformedPickupOption?.value;
        if (fallback) {
          setData(prev => ({ ...prev, shippingOption: fallback }));
        }
      }
    } catch (error) {
      console.error("Failed to get delivery options:", error);
      // On error, keep existing options but log the issue
    } finally {
      shippingCalcInFlightCountRef.current = Math.max(
        0,
        shippingCalcInFlightCountRef.current - 1
      );
      if (shippingCalcInFlightCountRef.current === 0) {
        setIsCalculatingShipping(false);
      }
    }
  }, [
    // Only include the essential dependencies that should trigger recalculation
    data.shippingType,
    data.billingZipcode,
    data.shippingZipcode,
    // F1 + oversized: quote-arranged states quote messenger from the
    // DELIVERY address.
    data.deliveryZipcode,
    cart?.id,
    (cart as any)?.skipShipping,
    // Re-quote when the cart's CONTENTS change, not just its id (2026-07-15).
    cartLineItemsSignature,
  ]);

  // Initialize shipping options but don't overwrite dynamically calculated options
  useEffect(() => {
    // Only initialize with original options if we don't have any updated options yet
    // Don't reset if we have dynamically loaded options from API
    if (updatedShippingOptions.length === 0 && shippingOptions.length > 0) {
      setUpdatedShippingOptions(shippingOptions);
    }
  }, [shippingOptions]); // Removed updatedShippingOptions from deps to prevent reset loop

  // Set initial shipping option and address on mount
  useEffect(() => {
    // Only update Stripe if we have a real Stripe shipping option (not our fake pickup ID)
    const isRealStripeOption =
      shippingPickupOption.originalOption ||
      shippingOptions.some(opt => opt.value === data.shippingOption);

    if (
      data.shippingOption &&
      data.shippingOption !== "" &&
      isRealStripeOption
    ) {
      if (
        data.shippingType === "pickup" ||
        data.shippingType === "oversized" ||
        data.shippingType === "multiple-calculated"
      ) {
        // For pickup/oversized/multiple-calculated: Update shipping address first (for tax calculation), then option
        (async () => {
          try {
            await checkout.updateShippingAddress(TRIBECA_ADDRESS);
            await checkout.updateShippingOption(data.shippingOption);
          } catch (error) {
            console.error("❌ Failed to initialize pickup shipping:", error);
          }
        })();
      } else {
        checkout.updateShippingOption(data.shippingOption);
      }
    }
  }, [
    checkout,
    data.shippingOption,
    data.shippingType,
    shippingPickupOption.originalOption,
    shippingOptions,
  ]); // Added missing dependencies

  async function confirm() {
    const shippingAddress = (() => {
      if (
        data.shippingType === "pickup" ||
        data.shippingType === "oversized" ||
        data.shippingType === "multiple-calculated"
      ) {
        return TRIBECA_ADDRESS;
      } else if (data.shippingType === "shipToMyAddress") {
        // Use billing address as shipping address
        return {
          name: `${data.firstName} ${data.lastName}`,
          address: {
            line1: data.billingStreet,
            line2: data.billingApartment || "",
            city: data.billingCity,
            state: data.billingState,
            postal_code: data.billingZipcode,
            country: data.billingCountry || "US",
          },
        };
      } else if (data.shippingType === "shipToOtherAddress") {
        // Use separate shipping address
        return {
          name: `${data.shippingFirstName} ${data.shippingLastName}`,
          address: {
            line1: data.shippingStreet,
            line2: data.shippingApartment || "",
            city: data.shippingCity,
            state: data.shippingState,
            postal_code: data.shippingZipcode,
            country: data.shippingCountry || "US",
          },
        };
      }
      return TRIBECA_ADDRESS; // Fallback
    })();

    // Ensure Stripe checkout uses the intended shipping option at payment time.
    const pickupOptionId = shippingPickupOption.value;
    const oversizedOptionId = shippingOversizedOption?.value;
    const fallbackNonPickupOption = updatedShippingOptions.find(
      option => option.value && option.value !== pickupOptionId
    )?.value;
    // Quote-arranged types: honor an explicitly selected REAL option (the
    // messenger) — forcing the $0 oversized marker here silently zeroed the
    // charge for customers who picked the priced messenger (fixed 7/18).
    const selectedRealOption = updatedShippingOptions.some(
      option =>
        option.value === data.shippingOption && option.value !== pickupOptionId
    )
      ? data.shippingOption
      : null;
    const resolvedShippingOption = (cart as any)?.skipShipping
      ? fallbackNonPickupOption || pickupOptionId
      : data.shippingType === "oversized" ||
          data.shippingType === "multiple-calculated"
        ? selectedRealOption || oversizedOptionId || pickupOptionId
        : data.shippingType === "pickup"
          ? pickupOptionId
          : data.shippingOption &&
              data.shippingOption !== pickupOptionId &&
              data.shippingOption !== ""
            ? data.shippingOption
            : fallbackNonPickupOption;

    const billingAddress = {
      name: `${data.firstName} ${data.lastName}`,
      address: {
        line1: data.billingStreet,
        line2: data.billingApartment || "",
        city: data.billingCity,
        state: data.billingState,
        postal_code: data.billingZipcode,
        country: data.billingCountry || "US",
      },
    };

    if (session.metadata.isFinalized !== "true") {
      throw new Error(
        "Checkout totals are still syncing. Click Save & Continue in Review Order and try again."
      );
    }

    if (resolvedShippingOption) {
      await checkout.updateShippingOption(resolvedShippingOption);
      if (resolvedShippingOption !== data.shippingOption) {
        setData(prev => ({ ...prev, shippingOption: resolvedShippingOption }));
      }
    }
    await checkout.updateShippingAddress(shippingAddress);

    return checkout.confirm({
      phoneNumber: data.phoneNumber,
      // GA (basil): confirm() rejects an email argument when the session
      // carries a customer whose email is set (every logged-in user since
      // the saved-cards attach) — the customer record owns the email. Only
      // pass it when the session doesn't already have one (guests).
      ...(checkout.email ? {} : { email: data.email }),
      billingAddress,
    });
  }

  async function finalizeReviewSession() {
    if (isFinalizingSessionRef.current) {
      return { type: "already_finalizing" as const };
    }
    const shippingAddress = (() => {
      if (
        data.shippingType === "pickup" ||
        data.shippingType === "oversized" ||
        data.shippingType === "multiple-calculated"
      ) {
        return TRIBECA_ADDRESS;
      } else if (data.shippingType === "shipToMyAddress") {
        return {
          name: `${data.firstName} ${data.lastName}`,
          address: {
            line1: data.billingStreet,
            line2: data.billingApartment || "",
            city: data.billingCity,
            state: data.billingState,
            postal_code: data.billingZipcode,
            country: data.billingCountry || "US",
          },
        };
      } else if (data.shippingType === "shipToOtherAddress") {
        return {
          name: `${data.shippingFirstName} ${data.shippingLastName}`,
          address: {
            line1: data.shippingStreet,
            line2: data.shippingApartment || "",
            city: data.shippingCity,
            state: data.shippingState,
            postal_code: data.shippingZipcode,
            country: data.shippingCountry || "US",
          },
        };
      }
      return TRIBECA_ADDRESS;
    })();

    const pickupOptionId = shippingPickupOption.value;
    const oversizedOptionId = shippingOversizedOption?.value;
    const fallbackNonPickupOption = updatedShippingOptions.find(
      option => option.value && option.value !== pickupOptionId
    )?.value;
    // Quote-arranged types: honor an explicitly selected REAL option (the
    // messenger) — forcing the $0 oversized marker here silently zeroed the
    // charge for customers who picked the priced messenger (fixed 7/18).
    const selectedRealOption = updatedShippingOptions.some(
      option =>
        option.value === data.shippingOption && option.value !== pickupOptionId
    )
      ? data.shippingOption
      : null;
    const resolvedShippingOption = (cart as any)?.skipShipping
      ? fallbackNonPickupOption || pickupOptionId
      : data.shippingType === "oversized" ||
          data.shippingType === "multiple-calculated"
        ? selectedRealOption || oversizedOptionId || pickupOptionId
        : data.shippingType === "pickup"
          ? pickupOptionId
          : data.shippingOption &&
              data.shippingOption !== pickupOptionId &&
              data.shippingOption !== ""
            ? data.shippingOption
            : fallbackNonPickupOption;

    const isStudioArranged =
      data.shippingType === "oversized" ||
      data.shippingType === "multiple-calculated" ||
      shippingStatus === "exceeds_table";

    const shippingInfo = {
      firstName:
        data.shippingType === "shipToOtherAddress"
          ? data.shippingFirstName || data.firstName || ""
          : data.firstName || "",
      lastName:
        data.shippingType === "shipToOtherAddress"
          ? data.shippingLastName || data.lastName || ""
          : data.lastName || "",
      companyName:
        data.shippingType === "shipToOtherAddress"
          ? data.shippingCompanyName || ""
          : data.companyName || "",
      street: shippingAddress.address.line1 || TRIBECA_ADDRESS.address.line1,
      apartment: shippingAddress.address.line2 || "",
      city: shippingAddress.address.city || "",
      state: shippingAddress.address.state || "",
      zipcode: shippingAddress.address.postal_code || "",
      // Phase B: the server prices/taxes by this (US default = pre-Phase-B
      // behavior everywhere).
      country: (shippingAddress.address as any).country || "US",
    };

    const hasShippingState = shippingInfo.state.trim().length > 0;
    const hasShippingZip = shippingInfo.zipcode.trim().length >= 5;
    const requiresDestinationForTax =
      data.shippingType === "shipToMyAddress" ||
      data.shippingType === "shipToOtherAddress";
    if (requiresDestinationForTax && (!hasShippingState || !hasShippingZip)) {
      return;
    }

    const isTaxExempt = !!(cart as any)?.taxExempt;
    // FIX 2026-08-27 (T-50997): the fingerprint carried no information about
    // what was IN the cart, so removing or editing an item after finalize left
    // this check matching and the short-circuit below returned
    // "already_finalized" — the server was never called, and the stale Stripe
    // session (still holding the old line items) stayed payable. T-50997 paid
    // a two-item session after one item had been removed. Mirrors the server's
    // getCartContentsSignature EXACTLY, sorted by id; the two strings must stay
    // byte-identical or every finalize needlessly rebuilds the session.
    const cartContents = JSON.stringify(
      [...((cart?.lineItems || []) as Array<{ id: string; quantity?: number }>)]
        .map(li => [li.id, li.quantity ?? 0] as [string, number])
        .sort((a, b) => a[0].localeCompare(b[0]))
    );
    const rawFingerprint = JSON.stringify({
      cartId: session.metadata.cartId,
      cartContents,
      shippingType: data.shippingType || "",
      state: (shippingInfo.state || "").trim().toUpperCase(),
      zipcode: (shippingInfo.zipcode || "").trim(),
      // Phase B: mirrors the server's fingerprint exactly — a US<->CA flip
      // must never reuse a finalized session.
      country:
        (shippingInfo.country || "US").trim().toUpperCase() === "CA"
          ? "CA"
          : "US",
      selectedShippingRateId: resolvedShippingOption || "",
      promotionCode: (data.promocode || "").trim().toLowerCase(),
      isTaxExempt,
    });
    // 2026-08-28: the server now stores a SHA-256 digest of this JSON in
    // Stripe metadata (raw JSON blew Stripe's 500-char metadata cap on a
    // 16-item cart and blocked checkout). Hash the same bytes here so the
    // short-circuit keeps matching. If WebCrypto is unavailable the compare
    // simply misses and the server does a harmless redundant re-finalize.
    let finalizationFingerprint = rawFingerprint;
    try {
      if (typeof crypto !== "undefined" && crypto.subtle) {
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(rawFingerprint)
        );
        finalizationFingerprint = Array.from(new Uint8Array(digest))
          .map(b => b.toString(16).padStart(2, "0"))
          .join("");
      }
    } catch {
      // keep rawFingerprint — comparison misses, server stays authoritative
    }
    if (
      session.metadata.isFinalized === "true" &&
      session.metadata.finalizedFingerprint === finalizationFingerprint
    ) {
      return { type: "already_finalized" as const };
    }

    const deliveryAddress = isStudioArranged
      ? {
          firstName: data.deliveryFirstName,
          lastName: data.deliveryLastName,
          companyName: data.deliveryCompanyName,
          street: data.deliveryStreet,
          apartment: data.deliveryApartment,
          city: data.deliveryCity,
          state: data.deliveryState,
          zipcode: data.deliveryZipcode,
          // Phase B: server tax + customs logic keys on this.
          country: data.deliveryCountry || "US",
        }
      : undefined;

    isFinalizingSessionRef.current = true;
    setIsFinalizingSession(true);
    try {
      const res = await StripeApi.finalizeCheckout({
        cartId: session.metadata.cartId,
        currentSessionId: session.id,
        parentOrderNo:
          data.parentOrderNo || session.metadata.parentOrderNo || "",
        whiteLabel: data.whiteLabel,
        gift: data.gift,
        note: data.note,
        shippingType: data.shippingType,
        shippingInfo,
        deliveryAddress,
        selectedShippingRateId: resolvedShippingOption,
        promotionCode: data.promocode || "",
      });
      if (res.data.session.id !== session.id) {
        onCheckoutReplaced?.(res.data);
        return { type: "session_replaced" as const };
      }
      // FIX 2026-08-05: the server's idempotent finalize path can return
      // the SAME session id already finalized server-side while this hook
      // still holds stale pre-finalize metadata — without a refresh,
      // isSessionFinalized stays false forever and the Pay guard loops.
      // Refresh local state the same way a replacement does.
      if (session.metadata.isFinalized !== "true") {
        onCheckoutReplaced?.(res.data);
        return { type: "session_replaced" as const };
      }
      return { type: "finalized" as const };
    } finally {
      isFinalizingSessionRef.current = false;
      setIsFinalizingSession(false);
    }
  }

  // Expose debugging function to window (remove in production)
  useEffect(() => {
    if (typeof window !== "undefined") {
      (
        window as typeof window & {
          debugCheckoutState: () => Record<string, unknown>;
        }
      ).debugCheckoutState = () => ({
        isUpdatingPricing,
        isCalculatingShipping,
        activeStep,
        shippingType: data.shippingType,
        shippingOption: data.shippingOption,
        clearLoadingState: () => {
          setIsUpdatingPricing(false);
        },
      });
    }
  }, [
    isUpdatingPricing,
    isCalculatingShipping,
    activeStep,
    data.shippingType,
    data.shippingOption,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    sessionStorage.setItem(
      "checkout_review_completed",
      reviewCompleted ? "true" : "false"
    );
  }, [reviewCompleted]);

  return {
    checkout,
    data,
    confirm,
    setData,
    isBillingSameAsShipping,
    shippingTypes: getShippingTypes(),
    billingOptions: getBillingOptions(checkout, data),
    userShippingAddress: user?.shippingDetails as Record<string, unknown>,
    userBillingAddress: user?.billingDetails as Record<string, unknown>,
    userEmail: user?.email,
    // Admin sessions check out on customers' behalf — see save-back guard.
    userIsAdmin: !!user?.isAdmin,
    shippingOptions: updatedShippingOptions,
    shippingPickupOption,
    shippingOversizedOption,
    activeStep,
    setActiveStep,
    collapsePanel,
    setCollapsePanel,
    isUpdatingPricing,
    setIsUpdatingPricing,
    calculateShippingPrices,
    isCalculatingShipping,
    isFinalizingSession,
    isSessionFinalized:
      session.metadata.isFinalized === "true" && !isFinalizingSession,
    finalizeReviewSession,
    cartSubtotal,
    cart,
    shippingStatus,
    reviewCompleted,
    setReviewCompleted,
    priceComparison,
  };
}

export function getShippingTypes(): ShippingType[] {
  return [
    { value: "shipToMyAddress", label: "Ship To My Billing Address" },
    { value: "shipToOtherAddress", label: "Ship To A Different Address" },
    { value: "pickup", label: "I'll Pick Up In Studio" },
  ];
}

export function getBillingOptions(
  checkout: { shippingAddress?: unknown },
  data: { shippingType: string }
) {
  const billingOptions: BillingAddressType[] = [
    { value: "billing-address", label: "Enter billing address" },
  ];
  if (data.shippingType === "shipToMyAddress" && checkout.shippingAddress)
    billingOptions.push({
      value: "shipping-address",
      label: "Use shipping address as billing address",
    });

  return billingOptions;
}

export const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

export function isValidUSState(stateValue: string): boolean {
  if (!stateValue || stateValue.trim() === "") {
    return false;
  }
  return US_STATES.some(state => state.value === stateValue.trim());
}

export const STATE_ZIP_RANGES: Record<string, [number, number][]> = {
  AL: [[350, 369]],
  AK: [[995, 999]],
  AZ: [[850, 865]],
  AR: [
    [716, 729],
    [755, 755],
  ],
  CA: [[900, 961]],
  CO: [[800, 816]],
  CT: [[60, 69]],
  DE: [[197, 199]],
  DC: [[200, 205]],
  FL: [[320, 349]],
  GA: [
    [300, 319],
    [398, 399],
  ],
  HI: [[967, 968]],
  ID: [[832, 838]],
  IL: [[600, 629]],
  IN: [[460, 479]],
  IA: [[500, 528]],
  KS: [[660, 679]],
  KY: [[400, 427]],
  LA: [[700, 714]],
  ME: [[39, 49]],
  MD: [[206, 219]],
  MA: [[10, 27]],
  MI: [[480, 499]],
  MN: [[550, 567]],
  MS: [[386, 397]],
  MO: [[630, 658]],
  MT: [[590, 599]],
  NE: [[680, 693]],
  NV: [[889, 898]],
  NH: [[30, 38]],
  NJ: [[70, 89]],
  NM: [[870, 884]],
  NY: [
    [5, 5],
    [63, 63],
    [100, 149],
  ],
  NC: [[270, 289]],
  ND: [[580, 588]],
  OH: [[430, 459]],
  OK: [[730, 749]],
  OR: [[970, 979]],
  PA: [[150, 196]],
  RI: [[28, 29]],
  SC: [[290, 299]],
  SD: [[570, 577]],
  TN: [[370, 385]],
  TX: [
    [750, 799],
    [885, 885],
  ],
  UT: [[840, 847]],
  VT: [[50, 59]],
  VA: [
    [201, 201],
    [220, 246],
  ],
  WA: [[980, 994]],
  WV: [[247, 268]],
  WI: [[530, 549]],
  WY: [[820, 831]],
};

export function isValidZipForState(zip: string, state: string): boolean {
  if (!zip || !state) return true;
  const cleaned = zip.replace(/\D/g, "");
  if (cleaned.length < 3) return true;
  const prefix = parseInt(cleaned.substring(0, 3), 10);
  const ranges = STATE_ZIP_RANGES[state];
  if (!ranges) return true;
  return ranges.some(([min, max]) => prefix >= min && prefix <= max);
}

// ── Canada (Phase B, 2026-07-23) ─────────────────────────────────────────
// Ship-to is US + CA (Michael's ruling; billing country is worldwide and
// goes straight to Stripe). Same false-allow philosophy as the US helpers:
// incomplete input makes no claim; the server validator does the fine-
// grained work.

export const SHIP_COUNTRIES = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
];

export const CA_PROVINCES = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

export function isValidCAProvince(provinceValue: string): boolean {
  if (!provinceValue || provinceValue.trim() === "") return false;
  return CA_PROVINCES.some(province => province.value === provinceValue.trim());
}

// A1A 1A1 shape (space optional). Complete-input check only — used on
// submit, not per keystroke.
export function isValidCAPostalFormat(postal: string): boolean {
  return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(
    String(postal || "").trim()
  );
}

// First letter of a Canadian postal code is province-determined (Canada
// Post FSA scheme — H/J both Québec, K–P all Ontario, X shared NT/NU).
// Mirrors the server's provincePostalMismatch; false-allow biased.
const PROVINCE_POSTAL_LETTERS: Record<string, string[]> = {
  NL: ["A"],
  NS: ["B"],
  PE: ["C"],
  NB: ["E"],
  QC: ["G", "H", "J"],
  ON: ["K", "L", "M", "N", "P"],
  MB: ["R"],
  SK: ["S"],
  AB: ["T"],
  BC: ["V"],
  NT: ["X"],
  NU: ["X"],
  YT: ["Y"],
};

export function isValidPostalForProvince(
  postal: string,
  province: string
): boolean {
  if (!postal || !province) return true;
  const first = String(postal).trim().charAt(0).toUpperCase();
  const letters =
    PROVINCE_POSTAL_LETTERS[String(province).trim().toUpperCase()];
  if (!letters || !first) return true;
  return letters.includes(first);
}
